/* eslint-disable no-param-reassign */
import AuthService from './auth-service';
import router from '../router';
import authReferrer from './auth-referrer';

export default {
  namespaced: true,

  state: {
    loading: true,
    currentUser: null,
  },

  getters: {
    loading: (state) => state.loading,
    currentUser: (state) => state.currentUser,
    currentUserUid: (state) =>
      state.currentUser
        ? state.currentUser.uid || null
        : null,
    authenticated: (state) => !!state.currentUser,
    authenticatedAsync: (state) => () =>
      new Promise((resolve) => {
        const interval = setInterval(() => {
          if (state.loading) {
            return;
          }

          clearInterval(interval);
          resolve(!!state.currentUser);
        }, 10);
      }),
  },

  mutations: {
    refresh(state, user) {
      state.currentUser = user;
      state.loading = false;
    },

    reset(state) {
      state.currentUser = null;
      state.loading = false;
    },

    loading(state, payload) {
      state.loading = !!payload;
    },
  },
  actions: {
    setup({ dispatch, getters, commit }) {
      AuthService.watchForAuth((authUser) => {
        const uid = authUser ? authUser.uid : null;
        const changed = getters.currentUserUid !== uid;

        if (changed) {
          AuthService.fetchCurrentUser()
            .then((currentUser) => {
              dispatch('refresh', currentUser);
            })
            .finally(() => {
              commit('loading', false);
            });
        } else {
          commit('loading', false);
        }
      });

      AuthService.getRedirectResult().catch((error) => {
        dispatch('core/validationError', error, {
          root: true,
        });
      });
    },

    async deleteAccount({ commit, dispatch }) {
      commit('loading', true);

      try {
        await AuthService.deleteAccount();
        dispatch('signOut');
        commit('loading', false);
        dispatch(
          'core/successMessage',
          'Account successfully deleted.',
          {
            root: true,
          },
        );
      } catch (error) {
        commit('loading', false);
        dispatch('core/validationError', error, {
          root: true,
        });
      }
    },

    signinWithEmailAndPassword(
      { commit, dispatch },
      { email, password, rememberMe },
    ) {
      if (!email || !password) {
        dispatch(
          'core/validationError',
          new Error(
            'Please inform your email and password.',
          ),
          {
            root: true,
          },
        );
        return;
      }

      commit('loading', true);

      return AuthService.signinWithEmailAndPassword(
        email,
        password,
        rememberMe,
      )
        .then(() => AuthService.fetchCurrentUser())
        .then((user) => {
          dispatch('refresh', user);
          commit('loading', false);
          router.push(authReferrer.get() || '/');
          authReferrer.remove();
        })
        .catch((error) => {
          commit('reset');
          dispatch('core/validationError', error, {
            root: true,
          });
        });
    },

    sendPasswordResetEmail({ commit, dispatch }, email) {
      if (!email) {
        return;
      }

      commit('loading', true);

      AuthService.sendPasswordResetEmail(email)
        .then(() => {
          commit('loading', false);
          dispatch(
            'core/successMessage',
            'Password reset email successfully sent.',
            {
              root: true,
            },
          );
          router.push('/auth/sign-in');
        })
        .catch((error) => {
          commit('reset');
          dispatch('core/validationError', error, {
            root: true,
          });
        });
    },

    signinWithGoogle({ commit, dispatch }, rememberMe) {
      commit('loading', true);

      return AuthService.signinWithGoogle(rememberMe)
        .then(() => AuthService.fetchCurrentUser())
        .then((user) => {
          dispatch('refresh', user);
          commit('loading', false);
        })
        .catch((error) => {
          commit('reset');
          return dispatch(
            'core/error',
            {
              message:
                'Not able to sign in/sign up, please try again later',
              error,
            },
            { root: true },
          );
        });
    },

    signinWithGitHub({ commit, dispatch }, rememberMe) {
      commit('loading', true);

      return AuthService.signinWithGitHub(rememberMe)
        .then(() => AuthService.fetchCurrentUser())
        .then((user) => {
          dispatch('refresh', user);
          commit('loading', false);
        })
        .catch((error) => {
          commit('reset');
          return dispatch(
            'core/error',
            {
              message:
                'Not able to sign in/sign up, please try again later',
              error,
            },
            { root: true },
          );
        });
    },

    signOut({ commit, dispatch }) {
      commit('loading', true);

      return Promise.resolve()
        .then(() => {
          return dispatch(`credits/removeWatchers`, null, {
            root: true,
          });
        })
        .then(() => {
          return dispatch('refresh', null);
        })
        .then(() => {
          return AuthService.signOut();
        })
        .then(() =>
          router.push('/').catch(() => {
            // do nothing because its on the same route
          }),
        )
        .catch((error) => {
          commit('reset');
          return dispatch(
            'core/error',
            { error },
            { root: true },
          );
        });
    },

    refresh({ commit, dispatch }, user) {
      commit('refresh', user);
      return dispatch('credits/setup', null, {
        root: true,
      });
    },
  },
};
